import { Column, Heading, Row, Space, Txt } from "@stenajs-webui/core";
import {
  Icon,
  Link,
  PrimaryButton,
  stenaClock,
  stenaLockClosed,
  stenaRefresh,
  stenaSignIn,
} from "@stenajs-webui/elements";
import type { FC } from "react";

import { CONTACT_URL } from "../../common/footer/Footer";
import { Information } from "../../common/texts";
import type { AuthError } from "../../services/authentication/authReducer";
import { useAuth } from "./AuthContext";

export interface AuthErrorBodyProps {
  error: AuthError;
}

export const AuthErrorBody: FC<AuthErrorBodyProps> = ({ error }) => {
  const auth = useAuth();

  if (
    error.type === "invalid_token" &&
    error.description.includes("current time") &&
    error.description.includes("is before issued")
  ) {
    return (
      <Column alignItems={"flex-start"}>
        <Row alignItems={"center"}>
          <Icon icon={stenaClock} />
          <Space />
          <Heading variant={"h2"}>{Information.AuthTimingHeading}</Heading>
        </Row>
        <Space />
        <Txt>{Information.AuthTimingExplanation}</Txt>
        <Space num={2} />
        <Row spacing={0.5}>
          <Txt size={"small"} variant={"bold"}>
            {Information.AuthTimingWindowsHelpHeader}
          </Txt>
        </Row>
        <Row spacing={0.5}>
          <Txt>{Information.AuthTimingStep1}</Txt>
        </Row>
        <Row spacing={0.5}>
          <Txt>{Information.AuthTimingStep2}</Txt>
        </Row>
        <Row spacing={0.5}>
          <Txt>{Information.AuthTimingStep3}</Txt>
        </Row>
        <Row spacing={0.5}>
          <Txt>{Information.AuthTimingStep4}</Txt>
        </Row>
        <Row spacing={0.5}>
          <Txt>{Information.AuthTimingStep5}</Txt>
        </Row>
        <Row spacing={0.5}>
          <Txt>{Information.AuthTimingStep6}</Txt>
        </Row>
        <Row spacing={0.5}>
          <Txt>{Information.AuthTimingStep7}</Txt>
        </Row>
      </Column>
    );
  }

  if (error.type === "timeout") {
    return (
      <Column alignItems={"flex-start"}>
        <Row alignItems={"center"}>
          <Icon icon={stenaClock} />
          <Space />
          <Heading variant={"h2"}>{Information.AuthTimeoutHeading}</Heading>
        </Row>
        <Space />
        <Txt>{Information.AuthTimeoutBody}</Txt>
        <Space num={2} />
        <Row>
          <PrimaryButton
            label={"Reload page"}
            leftIcon={stenaRefresh}
            onClick={() => window.location.reload()}
          />
        </Row>
      </Column>
    );
  }

  return (
    <Column alignItems={"flex-start"}>
      <Row alignItems={"center"}>
        <Icon icon={stenaLockClosed} />
        <Space />
        <Heading variant={"h2"}>{Information.AuthLoginFailedHeading}</Heading>
      </Row>
      <Space />
      <Txt>{Information.AuthLoginFailedBody}</Txt>
      <Space num={2} />
      <PrimaryButton
        label={"Go to login"}
        leftIcon={stenaSignIn}
        onClick={() => auth.logout()}
      />
      <Space num={2} />
      <Txt>
        Please <Link href={CONTACT_URL}>contact your local booking office</Link>{" "}
        if you are still not able to log in to Freight Portal and need
        assistance with your bookings.
      </Txt>
    </Column>
  );
};
